import React, { useState, useEffect, useCallback } from 'react';
import { AiOutlineLoading } from "react-icons/ai";
import { IoClose } from "react-icons/io5";
import { useTranslation } from 'react-i18next';

const DailyMeme = ({ isDarkMode, showMeme }) => {
  const [memeUrl, setMemeUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isEnlarged, setIsEnlarged] = useState(false);
  const { t } = useTranslation();

  const fetchMeme = useCallback(async () => {
    if (!showMeme) return;
    setIsLoading(true);
    setImageLoaded(false);
    setError(null);
    try {
      const response = await fetch('https://api.labcat.com.cn/meme/');
      if (!response.ok) {
        throw new Error('Failed to fetch meme');
      }
      const data = await response.json();
      setMemeUrl(`https://api.labcat.com.cn${data.url}`);
    } catch (err) {
      setError('Failed to load meme');
      console.error('Error fetching meme:', err);
    }
  }, [showMeme]);

  useEffect(() => {
    if (showMeme) {
      fetchMeme();
    } else {
      setMemeUrl(null);
      setImageLoaded(false);
    }
  }, [showMeme, fetchMeme]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setIsLoading(false);
  };

  const toggleEnlarged = () => {
    setIsEnlarged(!isEnlarged);
  };

  if (!showMeme) return null;

  return (
    <>
      {(isLoading || !imageLoaded) && (
        <div className="flex items-center justify-center py-8 rounded-2xl mt-3">
          <div className="flex flex-col items-center gap-2.5">
            <AiOutlineLoading className={`animate-spin text-2xl ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
            <p className={`text-xs font-medium tracking-wide ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{t('common.loading')}</p>
          </div>
        </div>
      )}
      
      <div className={`mt-3 rounded-2xl overflow-hidden transition-shadow duration-300 ${
        isDarkMode
          ? 'bg-white/[0.04] ring-1 ring-white/[0.06] shadow-[0_4px_24px_rgba(0,0,0,0.25)]'
          : 'bg-white/60 ring-1 ring-slate-200/60 shadow-[0_4px_24px_rgba(0,0,0,0.06)]'
      }`}>
        {error && <p className="text-center p-4 text-sm text-red-400">{error}</p>}
        {memeUrl && (
          <div className="homepage-scrollbar relative group max-h-[350px] overflow-y-auto overflow-x-hidden">
            <div className={`absolute top-2.5 right-2.5 z-10 rounded-lg px-2.5 py-1 text-[11px] font-medium tracking-wide opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
              'bg-black/60 text-white/90 backdrop-blur-sm'
            }`}>
              {t('common.click') || 'Click'} {t('common.toEnlarge') || 'to enlarge'}
            </div>
            <div
              className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer"
              onClick={toggleEnlarged}
            >
              <div className="absolute inset-0 bg-black/10" />
              <svg xmlns="http://www.w3.org/2000/svg" className="relative h-10 w-10 text-white/70 drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </div>
            <img
              src={memeUrl}
              alt="Daily Research Meme"
              className="w-full h-auto cursor-pointer"
              onLoad={handleImageLoad}
              onClick={toggleEnlarged}
              style={{ display: imageLoaded ? 'block' : 'none' }}
            />
          </div>
        )}
      </div>
      
      {isEnlarged && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/92 p-4 backdrop-blur-sm"
          onClick={toggleEnlarged}
        >
          <div className="relative flex max-h-[90vh] max-w-[95%] items-center justify-center">
            <button
              className="absolute -top-3 -right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white/80 backdrop-blur-md ring-1 ring-white/20 transition-all hover:bg-white/25 hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                toggleEnlarged();
              }}
            >
              <IoClose size={18} />
            </button>
            <img
              src={memeUrl}
              alt="Enlarged Daily Research Meme"
              className="max-h-[85vh] max-w-full rounded-lg object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default DailyMeme;
